import { useCallback, useEffect, useRef, useState } from "react";

function runsStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches === true
    || window.navigator.standalone === true;
}

function installPlatform() {
  const navigator = window.navigator;
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  return ios ? "ios" : "browser";
}

/** 保存瀏覽器一次性的安裝事件，供設定頁由使用者手動觸發。 */
export function usePwaInstall() {
  const promptRef = useRef(null);
  const [status, setStatus] = useState(() => runsStandalone() ? "installed" : "manual");

  useEffect(() => {
    const displayMode = window.matchMedia?.("(display-mode: standalone)");
    const syncDisplayMode = () => setStatus(runsStandalone()
      ? "installed"
      : promptRef.current ? "available" : "manual");
    const capturePrompt = (event) => {
      event.preventDefault();
      promptRef.current = event;
      setStatus("available");
    };
    const markInstalled = () => {
      promptRef.current = null;
      setStatus("installed");
    };
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", markInstalled);
    displayMode?.addEventListener?.("change", syncDisplayMode);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", markInstalled);
      displayMode?.removeEventListener?.("change", syncDisplayMode);
    };
  }, []);

  const install = useCallback(async () => {
    const prompt = promptRef.current;
    if (!prompt) return "unavailable";
    try {
      const result = await prompt.prompt();
      const choice = result?.outcome ? result : await prompt.userChoice;
      promptRef.current = null;
      setStatus(choice?.outcome === "accepted" || runsStandalone() ? "installed" : "manual");
      return choice?.outcome || "dismissed";
    } catch {
      promptRef.current = null;
      setStatus(runsStandalone() ? "installed" : "manual");
      return "failed";
    }
  }, []);

  return { status, platform: installPlatform(), install };
}
